import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { LoadingSpinner } from "../../../components/ui/loadingSpinner/loadingSpinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { useApiKeys } from "../../../hooks/useApiKeys";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { formattedDate } from "../../../lib/utils";

export const ApiKeysPage = () => {
  const { loading, apiKeys, createApiKey, deleteApiKey } = useApiKeys({
    fetchOnLoad: true,
  });
  const [isCreateApiKeyModalOpen, setIsCreateApiKeyModalOpen] = useState(false);
  const [deletingStates, setDeletingStates] = useState<boolean[]>(
    new Array(apiKeys.length).fill(false),
  );

  const handleDelete = async (index: number, id: string) => {
    const newDeletingStates = [...deletingStates];
    newDeletingStates[index] = true;
    setDeletingStates(newDeletingStates);
    await deleteApiKey(id);
    newDeletingStates[index] = false;
    setDeletingStates(newDeletingStates);
  };

  return (
    <div className="h-screen flex flex-col w-full mx-auto">
      <div className="px-4 mx-auto flex flex-col w-full max-w-4xl mt-24">
        <div className="flex justify-between items-center">
          <div className="text-2xl">API Keys</div>
          <Button onClick={() => setIsCreateApiKeyModalOpen(true)}>
            Create New API Key
          </Button>
        </div>
        {loading ? (
          <div className="flex justify-center items-center mt-8">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="mt-8 max-h-[75vh] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apiKeys.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.name || "N/A"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span>
                          {"*".repeat(item.key.length / 2 - 4) +
                            item.key.slice(-4)}
                        </span>
                        <Button
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(item.key);
                            toast.success("Copied API key to clipboard");
                          }}
                        >
                          Copy
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>{formattedDate(item.createdAt)}</TableCell>
                    <TableCell>
                      {item.lastUsed
                        ? formattedDate(item.lastUsed)
                        : "Never used"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        loading={deletingStates[index]}
                        onClick={() => handleDelete(index, item.id)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <CreateNewApiKeyModal
        isOpen={isCreateApiKeyModalOpen}
        setIsOpen={setIsCreateApiKeyModalOpen}
        onCreateApiKey={createApiKey}
      />
    </div>
  );
};

const CreateNewApiKeyModal = ({
  isOpen,
  setIsOpen,
  onCreateApiKey,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onCreateApiKey: (name: string) => void;
}) => {
  const [apiKeyName, setApiKeyName] = useState("");

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New API Key</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">API Key Name</Label>
            <Input
              id="name"
              value={apiKeyName}
              onChange={(e) => setApiKeyName(e.target.value)}
              placeholder="Enter API key name"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onCreateApiKey(apiKeyName);
              setApiKeyName("");
              setIsOpen(false);
            }}
          >
            Create
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
